import React, { createContext, useContext, useState, useEffect } from "react";
import i18next, { i18n, Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import deLocale from "../locals/de.json";
import enLocale from "../locals/en.json";
import deMessages from "../translations/de.json";
import enMessages from "../translations/en.json";
import { mergeMessages } from "../functions";
import {
  AnketaLanguages,
  AnketaTranslationOptions,
  I18nContextType,
} from "../types/context";

const de = mergeMessages(deLocale, deMessages);
const en = mergeMessages(enLocale, enMessages);

const resources: Resource = {
  en: { translation: en.messages },
  de: { translation: de.messages },
};

const i18nInstance = i18next.createInstance();

export const translateStatic = (
  key: string,
  lang: string,
  values?: Record<string, any>,
  defaultMessage?: string
): string => {
  const result = i18nInstance.t(key, { lng: lang, ...values, defaultValue: defaultMessage });
  return typeof result === "string" ? result : (defaultMessage || key);
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const DEFAULT_LANG = "de";

/** Keep <html lang> in sync with the active locale (a11y + correct hyphenation). */
const setHtmlLang = (lang: string) => {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", lang);
  }
};

export const AnketaI18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG);
  const [ready, setReady] = useState(false);

  const languages: Array<AnketaLanguages> = [
    { code: "en", label: "English", icon: "globe" },
    { code: "de", label: "Deutsch", icon: "flag" },
  ];

  useEffect(() => {
    i18nInstance
      .use(initReactI18next)
      .use(LanguageDetector)
      .init({
        resources,
        // No hardcoded `lng`: let the detector pick from localStorage first,
        // then the browser; fall back to German, then English. The detector
        // persists the choice so it survives reloads.
        fallbackLng: [DEFAULT_LANG, "en"],
        supportedLngs: ["en", "de"],
        nonExplicitSupportedLngs: true, // "de-DE" / "en-US" -> "de" / "en"
        detection: {
          order: ["localStorage", "navigator"],
          lookupLocalStorage: "anketa.lang",
          caches: ["localStorage"],
        },
        interpolation: { escapeValue: false },
      })
      .then(() => {
        const resolved = i18nInstance.resolvedLanguage || DEFAULT_LANG;
        setCurrentLang(resolved);
        setHtmlLang(resolved);
        setReady(true);
      });
  }, []);

  const setLanguage = (lang: string) => {
    i18nInstance.changeLanguage(lang); // also caches to localStorage via detector
    setCurrentLang(lang);
    setHtmlLang(lang);
  };

  const translate = (
    key: string,
    options?: AnketaTranslationOptions
  ): string => {
    try {
      // Pass defaultMessage as i18next `defaultValue` so a genuinely missing
      // key renders readable text instead of the raw dotted key string.
      const result = i18nInstance.t(key, {
        ...options?.values,
        defaultValue: options?.defaultMessage,
      });
      if (typeof result === "string") {
        return result;
      }
      if (options?.defaultMessage) {
        return options.defaultMessage;
      }
      return key;
    } catch {
      return options?.defaultMessage || key;
    }
  };

  if (!ready) return null;

  return (
    <I18nContext.Provider
      value={{
        i18n: i18nInstance,
        currentLang,
        setLanguage,
        translate,
        languages,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useAnketaI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useAnketaI18n must be used within an I18nProvider");
  }
  return context;
};
